// src/core/PhenomenonForm.js

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Save, AlertCircle } from 'lucide-react';
import { toPersianDigits, getSliderLabel } from './utils';

const sliderThumbClasses = {
    priority: {
        1: 'slider-thumb-orange', 2: 'slider-thumb-orange',
        3: 'slider-thumb-yellow', 4: 'slider-thumb-yellow',
        5: 'slider-thumb-lime',   6: 'slider-thumb-lime',
        7: 'slider-thumb-green',  8: 'slider-thumb-green',
        9: 'slider-thumb-blue',   10: 'slider-thumb-blue',
    },
    default: [
        'slider-thumb-red', 'slider-thumb-orange', 'slider-thumb-yellow',
        'slider-thumb-lime', 'slider-thumb-green', 'slider-thumb-blue'
    ]
};

const getSliderThumbClass = (value, isImportancePriority = false) => {
    if (value === 0) return 'slider-thumb-red';
    if (isImportancePriority) {
        return sliderThumbClasses.priority[value] || 'slider-thumb-blue';
    }
    return sliderThumbClasses.default[value] || 'slider-thumb-blue';
};

const PhenomenonForm = ({ phenomenon, answer, onAnswerChange, allPhenomena, onSave, onSaveAndNext, hasNext, hasPrev, onPrev, onShowExplanation, onShowFieldExplanation, onShowInterdependencyExplanationInput, completionData }) => {
  const [showInterdependencies, setShowInterdependencies] = useState(false);

  const renderProjectTypeInput = (keyPrefix, typeLabel, titlePlaceholder) => {
    const isCheckedKey = `is${keyPrefix}`; // e.g., isMegaProject
    const titleKey = `${keyPrefix.charAt(0).toLowerCase() + keyPrefix.slice(1)}Title`; // e.g., megaProjectTitle
    const explanationKey = `${keyPrefix.charAt(0).toLowerCase() + keyPrefix.slice(1)}Explanation`; // e.g., megaProjectExplanation

    const isChecked = !!answer[isCheckedKey];
    const titleValue = answer[titleKey] || '';
    const explanationValue = answer[explanationKey] || '';

    const explanationTitle = (
      <>
        لطفاً شرح تکمیلی برای «{typeLabel}» پیشنهادی با عنوان «<span className="text-blue-700">{titleValue}</span>» را بنویسید.
      </>
    );

    return (
      <div className="flex items-center space-x-4 space-x-reverse mb-2">
        <label className="inline-flex items-center w-40">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => {
              const checked = e.target.checked;
              onAnswerChange(isCheckedKey, checked);
              if (!checked) {
                // Clear title and explanation if unchecked
                onAnswerChange(titleKey, '');
                onAnswerChange(explanationKey, '');
              }
            }}
            className="form-checkbox text-teal-600 h-5 w-5"
          />
          <span className="mr-2 text-gray-700 font-medium">{typeLabel}</span>
        </label>
        <input
          type="text"
          value={titleValue}
          onChange={(e) => onAnswerChange(titleKey, e.target.value)}
          disabled={!isChecked}
          className="flex-grow p-3 border rounded-lg focus:ring-teal-500 focus:border-teal-500 text-right border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
          placeholder={titlePlaceholder}
        />
        <div className="w-[80px] h-6 flex items-center justify-end">
            <button
              onClick={() => onShowFieldExplanation(explanationKey, explanationTitle, explanationValue)}
              className={`inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-semibold underline
                           bg-blue-50 px-2 py-1 rounded shadow-sm transition-all duration-300 ease-in-out
                           ${isChecked && titleValue.trim().length > 0 ? '' : 'opacity-0 pointer-events-none'}`}
              tabIndex={(isChecked && titleValue.trim().length > 0) ? 0 : -1}
            >
              <AlertCircle size={16} className="ml-1" />
              شرح...
            </button>
        </div>
      </div>
    );
  };


  const renderSlider = (field, label, min = 0, max = 5, isImportancePriority = false, showExplanationButton = false, explanationTitleGenerator = null, labelSetKey = 'default') => {
    const currentValue = answer[field];
    const numericValue = typeof currentValue === 'number' ? currentValue : parseInt(currentValue);
    const finalValue = isNaN(numericValue) ? min : numericValue;
    
    const isIncreaseDecreaseNoEffect = labelSetKey === 'increaseDecrease' && finalValue === 3;
    const isDefaultNoImpact = labelSetKey === 'default' && finalValue === 1; // Condition for "هیچ"

    const shouldShowButton = showExplanationButton &&
                             finalValue !== 0 && // Hides for "نظری ندارم"
                             !isIncreaseDecreaseNoEffect && // Hides for "بی اثر"
                             !isDefaultNoImpact && // Hides for "هیچ"
                             explanationTitleGenerator;
    
    const handleTitleClick = () => {
        if (shouldShowButton) {
            onShowFieldExplanation(field, explanationTitleGenerator(phenomenon.name, finalValue), answer[`${field}Explanation`] || '');
        }
    };

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between items-center">
          <div 
             className={`flex-grow ${shouldShowButton ? 'cursor-pointer' : ''}`}
             onClick={handleTitleClick}
          >
            {label}
            {' '}
            <span className="mr-2">{getSliderLabel(finalValue, isImportancePriority, labelSetKey)}</span>
          </div>
          <div className="w-[80px] h-6 flex items-center justify-end">
            <button
              onClick={() => onShowFieldExplanation(field, explanationTitleGenerator(phenomenon.name, finalValue), answer[`${field}Explanation`] || '')}
              className={`inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-semibold underline
                           bg-blue-50 px-2 py-1 rounded shadow-sm transition-all duration-300 ease-in-out
                           ${shouldShowButton ? '' : 'opacity-0 pointer-events-none'}`}
              tabIndex={shouldShowButton ? 0 : -1}
            >
              <AlertCircle size={16} className="ml-1" />
              شرح...
            </button>
          </div>
        </label>
        <input
          type="range"
          min={min}
          max={max}
          value={finalValue}
          onChange={(e) => onAnswerChange(field, parseInt(e.target.value))}
          className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${getSliderThumbClass(finalValue, isImportancePriority)}`}
        />
      </div>
    );
  };

  const selectedInterdependencies = answer['interdependencies'] || [];
  const initialDisplayCount = 3;
  const safeAllPhenomena = Array.isArray(allPhenomena) ? allPhenomena : [];
  const filteredPhenomenaForDisplay = safeAllPhenomena.filter(p => p.type === 'phenomenon' && p.id !== phenomenon.id);
  const displayedInterdependenciesOptions = showInterdependencies
    ? filteredPhenomenaForDisplay
    : filteredPhenomenaForDisplay.slice(0, initialDisplayCount);
  const hasMoreInterdependencies = filteredPhenomenaForDisplay.length > initialDisplayCount;

  const handleInterdependencyCheckboxChange = (e) => {
    const value = e.target.value;
    const current = answer['interdependencies'] || [];
    const newSelection = e.target.checked
      ? [...current, value]
      : current.filter(item => item !== value);
    onAnswerChange('interdependencies', newSelection);
  };

  const operationalDefinitionCharCount = answer.operationalDefinition?.length || 0;

  if (!phenomenon) {
    return <div className="text-center text-gray-500">پدیده‌ای انتخاب نشده است.</div>;
  }

  const otherSectionsOptions = [
    { key: 'governance', label: 'نحوه اداره' },
    { key: 'structure', label: 'ساختار و سازماندهی' },
    { key: 'humanResources', label: 'منابع انسانی' },
    { key: 'serviceProcesses', label: 'سازوکارها و فرآیندهای ارائه خدمت' },
    { key: 'planGovernance', label: 'راهبری و اداره طرح' },
    { key: 'technology', label: 'فناوری' },
    { key: 'other', label: 'سایر' },
  ];

  const opportunityTitleGenerator = (phenomenonName, value) => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" در سازمان فرصت {getSliderLabel(value)} ایجاد می‌کند؟
    </>
  );
  const crisisTitleGenerator = (phenomenonName, value) => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" در سازمان بحران {getSliderLabel(value)} ایجاد می‌کند؟
    </>
  );
  const capacityTitleGenerator = (phenomenonName, value) => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" در سازمان ظرفیت {getSliderLabel(value)} ایجاد می‌کند؟
    </>
  );
  const driverTitleGenerator = (phenomenonName, value) => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" در سازمان پیشران {getSliderLabel(value)} است؟
    </>
  );
  const transformativeChangeTitleGenerator = (phenomenonName, value) => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" در سازمان تغییر تحول‌آفرین {getSliderLabel(value)} ایجاد می‌کند؟
    </>
  );
  const genericImpactTitleGenerator = (phenomenonName, fieldLabel, value, labelSetKey = 'default') => (
    <>
      لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" بر {fieldLabel} تأثیر {getSliderLabel(value, false, labelSetKey)} دارد؟
    </>
  );

  return (
    <div className="p-6 bg-white rounded-lg shadow-md relative">
      <div className="sticky top-0 z-10 bg-white pt-6 pb-4 -mx-6 px-6 rounded-t-lg shadow-sm flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-blue-700 text-center flex-grow">تحلیل پدیده: {phenomenon.name}</h2>
        <button
          onClick={() => onShowExplanation(phenomenon)}
          className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
        >
          توضیحات
        </button>
      </div>

      <div className="mb-6">
        <label htmlFor="operationalDefinition" className="block text-sm font-medium text-gray-700 mb-1">
          شرح (تعریف عملیاتی)
        </label>
        <textarea
          id="operationalDefinition"
          rows="4"
          value={answer.operationalDefinition || ''}
          onChange={(e) => onAnswerChange('operationalDefinition', e.target.value)}
          className="w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300"
          placeholder="لطفاً شرح دقیق و کاربردی از این پدیده را با تأکید بر جنبه‌های مرتبط با سازمان تأمین اجتماعی بنویسید."
        ></textarea>
        <div className="text-xs text-gray-500 mt-1 flex justify-between">
          <span>{`تعداد کاراکتر: ${toPersianDigits(operationalDefinitionCharCount)}`}</span>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
        <h3 className="text-xl font-semibold mb-4 text-blue-800">۱. ارزیابی کلی پدیده</h3>
        <p className="mb-4 text-gray-600">ارزیابی کلی شما از این پدیده چیست؟</p>
        {renderSlider('hasImpact', 'آیا این پدیده بر سازمان تأثیر معنادار دارد؟', 0, 5, false, true, (phenomenonName, value) => (
          <>
            لطفاً شرح دهید چرا و چگونه پدیده "<span className="text-blue-700">{phenomenonName}</span>" بر سازمان تأثیر {getSliderLabel(value)} دارد؟
          </>
        ))}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            افق زمانی اصلی تأثیر این پدیده بر سازمان چقدر است؟
          </label>
          <div className="flex space-x-4 space-x-reverse">
            {['کوتاه‌مدت', 'میان‌مدت', 'بلندمدت'].map(time => (
              <label key={time} className="inline-flex items-center">
                <input
                  type="radio" name="timeHorizon" value={time}
                  checked={answer.timeHorizon === time}
                  onChange={(e) => onAnswerChange('timeHorizon', e.target.value)}
                  className="form-radio text-blue-600 h-5 w-5 rounded"
                />
                <span className="mr-2 text-gray-700">{time}</span>
              </label>
            ))}
          </div>
        </div>
        {renderSlider('importancePriority', 'برای اهمیت و اولویت این پدیده چه رتبه‌ای قائل هستید؟ (۰ تا ۱۰)', 0, 10, true)}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1 flex justify-between items-center">
            <span>این پدیده با کدام‌یک از پدیده‌های دیگر ارتباط معنادار دارد؟</span>
            {selectedInterdependencies.length > 0 && (
              <button
                onClick={() => onShowInterdependencyExplanationInput(phenomenon.name, selectedInterdependencies, answer.interdependencyExplanation || '')}
                className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-semibold underline bg-blue-50 px-2 py-1 rounded shadow-sm transition-all duration-300 ease-in-out"
              >
                <AlertCircle size={16} className="ml-1" />
                شرح...
              </button>
            )}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto border p-2 rounded-lg bg-gray-50">
            {displayedInterdependenciesOptions.map(option => (
              <label key={option.id} className="inline-flex items-center text-sm">
                <input
                  type="checkbox" value={option.name}
                  checked={selectedInterdependencies.includes(option.name)}
                  onChange={handleInterdependencyCheckboxChange}
                  className="form-checkbox text-blue-600 h-4 w-4 rounded"
                />
                <span className="mr-2 text-gray-700">{option.name}</span>
              </label>
            ))}
          </div>
          {hasMoreInterdependencies && (
            <button
              onClick={() => setShowInterdependencies(!showInterdependencies)}
              className="mt-2 text-blue-700 hover:text-blue-900 font-semibold text-sm"
            >
              {showInterdependencies ? 'نمایش کمتر' : 'نمایش بیشتر'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-green-50 p-4 rounded-lg mb-6 border border-green-200">
        <h3 className="text-xl font-semibold mb-4 text-green-800">۲. تحلیل و اثرسنجی (شدت تأثیر از ۰ تا ۵)</h3>
        <p className="mb-4 text-gray-600">با تحلیل و اثرسنجی پدیده بر سازمان تأمین اجتماعی مشخص کنید این پدیده برای سازمان واجد کدام‌یک از خصوصیت‌های زیر است و به چه میزان تأثیر دارد؟</p>
        {renderSlider('opportunity', 'فرصت', 0, 5, false, true, opportunityTitleGenerator)}
        {renderSlider('crisis', 'بحران (مسئله، مشکل، چالش، تهدید و مخاطره)', 0, 5, false, true, crisisTitleGenerator)}
        {renderSlider('capacity', 'ظرفیت (قابلیت و موقعیت)', 0, 5, false, true, capacityTitleGenerator)}
        {renderSlider('driver', 'پیشران', 0, 5, false, true, driverTitleGenerator)}
        {renderSlider('transformativeChange', 'تغییر تحول‌آفرین (افق‌گشا و موج‌ساز)', 0, 5, false, true, transformativeChangeTitleGenerator)}
      </div>

      <div className="bg-yellow-50 p-4 rounded-lg mb-6 border border-yellow-200">
        <h3 className="text-xl font-semibold mb-4 text-yellow-800">۳. تأثیر بر بخش‌های مختلف سازمان (شدت تأثیر از ۰ تا ۵)</h3>
        <p className="mb-4 text-gray-600">این پدیده چه تأثیری، چگونه و به چه میزان بربخش‌های مختلف سازمان می‌گذارد؟</p>
        <h4 className="text-lg font-medium mb-3 text-yellow-700">الف) بیمه‌ای</h4>
        {renderSlider('insuranceInput', 'ورودی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'ورودی بیمه‌ای', value))}
        {renderSlider('insuranceOutput', 'خروجی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'خروجی بیمه‌ای', value))}
        {renderSlider('insuranceComposition', 'تغییر ترکیب (مثال: کاهش تعداد بیمه‌شدگان اجباری، افزایش تعداد بیمه‌شدگان اختیاری، توافقی و ...)', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'تغییر ترکیب بیمه‌ای', value))}
        {renderSlider('insuranceResources', 'منابع', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'منابع بیمه‌ای', value, 'increaseDecrease'), 'increaseDecrease')}
        {renderSlider('insuranceExpenses', 'مصارف', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'مصارف بیمه‌ای', value, 'increaseDecrease'), 'increaseDecrease')}
        <div className="mb-4">
          <label htmlFor="insuranceServiceDelivery" className="block text-sm font-medium text-gray-700 mb-1">
            {`پدیده «${phenomenon.name}» چه تأثیری بر چگونگی ارائه خدمات بیمه‌ای از سوی سازمان دارد؟`}
          </label>
          <textarea
              id="insuranceServiceDelivery"
              rows="3"
              value={answer.insuranceServiceDelivery || ''}
              onChange={(e) => onAnswerChange('insuranceServiceDelivery', e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300"
              placeholder="شرح تأثیر..."
          ></textarea>
        </div>

        <h4 className="text-lg font-medium mt-6 mb-3 text-yellow-700">ب) درمان</h4>
        {renderSlider('treatmentInput', 'ورودی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'ورودی درمان', value))}
        {renderSlider('treatmentOutput', 'خروجی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'خروجی درمان', value))}
        {renderSlider('treatmentComposition', 'تغییر ترکیب (مثال: کاهش بار مراجعات ملکی یا مستقیم، افزایش بار مراجعات غیر مستقیم و ...)', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'تغییر ترکیب درمان', value))}
        {renderSlider('treatmentResources', 'منابع', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'منابع درمان', value, 'increaseDecrease'), 'increaseDecrease')}
        {renderSlider('treatmentExpenses', 'مصارف', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'مصارف درمان', value, 'increaseDecrease'), 'increaseDecrease')}
        <div className="mb-4">
            <label htmlFor="treatmentServiceDelivery" className="block text-sm font-medium text-gray-700 mb-1">
                {`پدیده «${phenomenon.name}» چه تأثیری بر چگونگی ارائه خدمات درمانی از سوی سازمان دارد؟`}
            </label>
            <textarea
                id="treatmentServiceDelivery"
                rows="3"
                value={answer.treatmentServiceDelivery || ''}
                onChange={(e) => onAnswerChange('treatmentServiceDelivery', e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right border-gray-300"
                placeholder="شرح تأثیر..."
            ></textarea>
        </div>

        <h4 className="text-lg font-medium mt-6 mb-3 text-yellow-700">ج) سرمایه‌گذاری</h4>
        {renderSlider('investmentProfit', 'سود یا بازدهی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'سود یا بازدهی سرمایه‌گذاری', value))}
        {renderSlider('investmentPortfolio', 'تغییر پرتفوی سرمایه‌گذاری (مثال: حرکت از سرمایه‌گذاری در حامل‌های انزژی فسیلی به سمت انرژی‌های تجدیدپذیر و ...)', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'تغییر پرتفوی سرمایه‌گذاری', value))}
        {renderSlider('investmentFunding', 'نحوه تأمین مالی (مثال: استفاده از رمزارزها، روش‌های تأمین مالی نوظهور، اوراق گام و ...)', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'نحوه تأمین مالی سرمایه‌گذاری', value))}

        <h4 className="text-lg font-medium mt-6 mb-3 text-yellow-700">د) سایر بخش‌ها</h4>
        <p className="mb-4 text-gray-600">این پدیده بر کدام‌یک از بخش‌های زیر و چگونه تأثیر می‌گذارد؟</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {otherSectionsOptions.map(({ key, label }) => {
            const fieldName = `otherSection_${key}`;
            const explanationFieldName = `${fieldName}Explanation`;
            const isChecked = !!answer[fieldName];

            const explanationTitle = key === 'other'
              ? <>لطفاً شرح دهید پدیده "<span className="text-blue-700">{phenomenon.name}</span>" بر چه بخش‌های دیگری و چگونه اثر می‌گذارد؟</>
              : <>لطفاً شرح دهید چگونه پدیده "<span className="text-blue-700">{phenomenon.name}</span>" بر "{label}" تأثیر می‌گذارد؟</>;

            return (
              <div key={fieldName} className="flex items-center" style={{ minHeight: '40px' }}>
                <label className="inline-flex items-center text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      onAnswerChange(fieldName, e.target.checked);
                      if (!e.target.checked) {
                        onAnswerChange(explanationFieldName, ''); // Clear explanation if unchecked
                      }
                    }}
                    className="form-checkbox text-blue-600 h-4 w-4 rounded"
                  />
                  <span className="mr-2 text-gray-700">{label}</span>
                </label>
                <div className="flex-grow flex items-center justify-start mr-4">
                  {isChecked && (
                    <button
                      onClick={() => onShowFieldExplanation(fieldName, explanationTitle, answer[explanationFieldName] || '')}
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-semibold underline bg-blue-50 px-2 py-1 rounded shadow-sm transition-all duration-300 ease-in-out"
                    >
                      <AlertCircle size={16} className="ml-1" />
                      شرح...
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-orange-50 p-4 rounded-lg mb-6 border border-orange-200">
        <h3 className="text-xl font-semibold mb-4 text-orange-800">۴. تأثیر بر اهداف کلان سازمان (شدت تأثیر از ۰ تا ۵)</h3>
        <p className="mb-4 text-gray-600">این پدیده چه تأثیری بر اهداف کلان سازمان و سازوکار و فرایندهای اجرایی و نحوه ارائه خدمات (به عنوان مثال درون‌سپاری یا برون‌سپاری فعالیت‌ها، خروج از تصدی‌گری، هوش‌مند‌سازی و خدمات دیجیتال و غیرحضوری) می‌گذارد؟</p>
        {renderSlider('coverageInclusivity', 'فراگیری پوشش', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'فراگیری پوشش', value))}
        {renderSlider('serviceIntegrity', 'جامعیت خدمات', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'جامعیت خدمات', value))}
        {renderSlider('benefitSufficiency', 'کفایت مزایا', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'کفایت مزایا', value))}
        {renderSlider('financialStability', 'پایداری مالی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'پایداری مالی', value))}
        {renderSlider('efficiency', 'کارآمدی (کارایی، اثربخشی، بهره‌وری، کیفیت خدمات و رضایتمندی)', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'کارآمدی', value))}
        {renderSlider('insuranceJustice', 'عدالت بیمه‌ای', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'عدالت بیمه‌ای', value))}
      </div>


      <div className="bg-purple-50 p-4 rounded-lg mb-6 border border-purple-200">
        <h3 className="text-xl font-semibold mb-4 text-purple-800">۵. راهکارها و ایده‌ها</h3>
        <div className="mb-4">
          <label htmlFor="proposedIdea" className="block text-sm font-medium text-gray-700 mb-1">ایده یا راهکار پیشنهادی شما برای مواجهه با این پدیده چیست؟ (برای حذف یا کاهش اثرات منفی و جذب و افزایش اثرات مثبت پدیده بر سازمان تأمین اجتماعی)</label>
          <textarea id="proposedIdea" rows="4"
            value={answer.proposedIdea || ''} onChange={(e) => onAnswerChange('proposedIdea', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right"
            placeholder="ایده یا راهکار کلیدی خود را توضیح دهید."
          ></textarea>
        </div>

        <h4 className="text-lg font-medium mb-3 text-purple-700">ارزیابی ایده پیشنهادی (شدت از ۰ تا ۵)</h4>
        {renderSlider('ideaExecutability', 'قابلیت اجرا', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'قابلیت اجرای ایده پیشنهادی', value))}
        {renderSlider('ideaAcceptability', 'مقبولیت و پذیرش ذی‌نفعان', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'مقبولیت ایده پیشنهادی', value))}
        {renderSlider('ideaFeasibility', 'فراهم بودن مقدورات و پیش‌نیازی‌ها', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'فراهم بودن مقدورات ایده پیشنهادی', value))}
        {renderSlider('ideaHighImpact', 'تأثیرگذاری بالا', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'تأثیرگذاری ایده پیشنهادی', value))}
        {renderSlider('ideaPracticalNecessity', 'ضرورت عملی', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'ضرورت عملی ایده پیشنهادی', value))}
        {renderSlider('ideaTheoreticalImportance', 'اهمیت نظری', 0, 5, false, true, (phenomenonName, value) => genericImpactTitleGenerator(phenomenonName, 'اهمیت نظری ایده پیشنهادی', value))}
      </div>

      <div className="bg-teal-50 p-4 rounded-lg mb-6 border border-teal-200">
        <h3 className="text-xl font-semibold mb-4 text-teal-800">۶. برای تحقق ایده پیشنهادی شما چه ابرپروژه‌ها، پروژه‌ها و برنامه‌های عملیاتی باید اجرا شود؟</h3>
        
        {renderProjectTypeInput('MegaProject', 'ابرپروژه', 'نام پیشنهادی ابرپروژه...')}
        {renderProjectTypeInput('Project', 'پروژه', 'نام پیشنهادی پروژه...')}
        {renderProjectTypeInput('OperationalProgram', 'برنامه عملیاتی', 'نام پیشنهادی برنامه عملیاتی...')}
        
        <div className="mt-6">
            <label htmlFor="legalDraft" className="block text-sm font-medium text-gray-700 mb-1">
                متن پیش‌نویس احکام قانونی و مقرراتی لازم برای تحقق این ایده را بنویسید.
            </label>
            <textarea
                id="legalDraft"
                rows="4"
                value={answer.legalDraft || ''}
                onChange={(e) => onAnswerChange('legalDraft', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-teal-500 focus:border-teal-500 text-right"
                placeholder="شرح احکام و مقررات..."
            ></textarea>
        </div>
      </div>

      <div className="bg-cyan-50 p-4 rounded-lg mb-6 border border-cyan-200">
        <label htmlFor="ideaRequirements" className="block text-xl font-semibold mb-4 text-cyan-800">
          ۷- الزامات (مقدمات، پیش‌نیازها و اقتضائات) تحقق ایده پیشنهادی خود را مرقوم فرمائید.
        </label>
        <textarea
          id="ideaRequirements"
          rows="4"
          value={answer.ideaRequirements || ''}
          onChange={(e) => onAnswerChange('ideaRequirements', e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500 text-right"
          placeholder="شرح الزامات..."
        ></textarea>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-800">۸. سایر توضیحات و ملاحظات</h3>
        <div className="mb-4">
          <label htmlFor="otherNotes" className="block text-sm font-medium text-gray-700 mb-1">توضیحات تکمیلی:</label>
          <textarea id="otherNotes" rows="4"
            value={answer.otherNotes || ''} onChange={(e) => onAnswerChange('otherNotes', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-right"
            placeholder="یادداشت‌های اضافی..."
          ></textarea>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onPrev} disabled={!hasPrev}
          className={`flex items-center bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out ${
            !hasPrev ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-400 transform hover:scale-105'
          }`}
        >
          <ChevronRight className="ml-2" size={20} />
          پدیده قبلی
        </button>
        <div className="flex space-x-4 space-x-reverse">
          <button
            onClick={onSave}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105"
          >
            <Save className="inline-block ml-2" size={20} />
            ذخیره
          </button>
          <button
            onClick={onSaveAndNext} disabled={!hasNext}
            className={`flex items-center bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out ${
              !hasNext ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-800 transform hover:scale-105'
            }`}
          >
            ذخیره و پدیده بعدی
            <ChevronLeft className="mr-2" size={20} />
          </button>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-gray-800 text-white p-2 mt-8 rounded-t-lg shadow-lg">
          <div className="flex items-center justify-center">
              <span className="text-sm font-semibold ml-4">میزان تکمیل این پدیده:</span>
              <div className="w-1/2 bg-gray-600 rounded-full h-4">
                  <div
                      className="bg-green-500 h-4 rounded-full transition-all duration-300"
                      style={{ width: `${completionData.percentage}%` }}
                  ></div>
              </div>
              <span className="text-sm font-bold mr-4">{toPersianDigits(completionData.percentage.toFixed(0))}%</span>
          </div>
      </div>
    </div>
  );
};

export default PhenomenonForm;
